import React, { useEffect, useState, useMemo } from "react";
import { Col, Row, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import { mapUserRoles } from "../../../../functions/useRoleMapping";
import useHandleBlur from "../../../../functions/useHandleBlur";
import { useFormState } from "../../../../functions/useFormState";
import useTbdCheck from "../../../../functions/useTbdCheck";
import useDerateCalc from "../../../../functions/useDerateCalc";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../../redux/slices/apiUrlSlice";
import { selectPumps, selectPumpDetails, selectFieldDescriptions } from "../../../../redux/selectors";
import { selectApiStatus } from "../../../../redux/slices/apiStatusSlice";
import { isValidTpl } from "../../../../utils/validateTpl";
import { normalizePumpId } from "./pumpIdHelpers";

const CustomPump = (zone) => {
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "pumpDetails";

  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectPumps);
  const pumpDetails = useSelector(selectPumpDetails);
  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const { formData, setFormData } = useFormState({});

  const elevationDerate = useDerateCalc();

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const customPumpDetails = useMemo(() => {
    return pumpDetails.find((pump) => 
      pump.PumpID && !["main", "pm", "jockey", "water feature", "transfer", "recirculation", "trash", "sump"].includes(normalizePumpId(pump.PumpID)) &&
      pump.Zone === activetab
    );
  }, [pumpDetails, activetab]);

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    if (customPumpDetails) {
      setFormData({
        ID: customPumpDetails.ID || "",
        pumpWatertronicsPartNumber:
          customPumpDetails.PumpWatertronicsPartNumber || "",
        pumpManufacturer: customPumpDetails.PumpManufacturer || "",
        pumpManufacturerModelNumber:
          customPumpDetails.PumpManufacturerModelNumber || "",
        pumpType: customPumpDetails.PumpType || "",
        pumpEfficiency: customPumpDetails.PumpEfficiency || "",
        pumpTDH: customPumpDetails.PumpTDH || "",
        pumpFlow: customPumpDetails.PumpFlow || "",
        pumpTPL: customPumpDetails.PumpTPL || "",
        pumpStages: customPumpDetails.PumpStages || "",
        pumpRPM: customPumpDetails.PumpRPM || "",
        pumpDischargeSize: customPumpDetails.PumpDischargeSize || "",
        pumpImpellerTrim: customPumpDetails.PumpImpellerTrim || "",
        additionalCheckValve: customPumpDetails.AdditionalCheckValve || "",
        motorWatertronicsPartNumber:
          customPumpDetails.MotorWatertronicsPartNumber || "",
        motorManufacturer: customPumpDetails.MotorManufacturer || "",
        motorManufacturerModelNumber:
          customPumpDetails.MotorManufacturerModelNumber || "",
        motorHP: customPumpDetails.MotorHP || "",
        motorEfficiency: customPumpDetails.MotorEfficiency || "",
        motorEnclosure: customPumpDetails.MotorEnclosure || "",
        motorFrame: customPumpDetails.MotorFrame || "",
        motorFLA: customPumpDetails.MotorFLA || "",
        motorCouplingType: customPumpDetails.MotorCouplingType || "",
        motorCouplingSize: customPumpDetails.MotorCouplingSize || "",
        motorWindingHeaters: customPumpDetails.MotorWindingHeaters || "",
        motorTempSwitch: customPumpDetails.MotorTempSwitch || "",
        motorVibrationSensor: customPumpDetails.MotorVibrationSensor || "",
        pumpOneConstruction: jobDetails.PumpOneConstruction || "",
        pumpTwoConstruction: jobDetails.PumpTwoConstruction || "",
        startMethod: customPumpDetails.StartMethod ?? "",
        comments: customPumpDetails.Comments || "",
        quantity: customPumpDetails.Quantity || "",
        elevationDerate: elevationDerate,
      });
    }
  }, [customPumpDetails, setFormData, jobDetails, elevationDerate]);

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
  });

  const [tplError, setTplError] = useState(false);
  const apiStatus = useSelector(selectApiStatus);
  const loading = apiStatus[sectionName] !== "success";

  useEffect(() => {
    setRoles((prevRoles) => ({ ...prevRoles, ...mapUserRoles(userRoles) }));
  }, [userRoles]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly);
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly;

  const { tbdFields } = useTbdCheck(formData, zone);
  const zoneTbdFields = zone ? tbdFields?.[zone] : tbdFields;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showNotification && (
        <div className={`save-notification ${saveStatus.includes("Failed") ? "error" : ""}`}>
          {saveStatus}
        </div>
      )}
      <div>
        <Row className="mb-3">
          <Col>
            <Form.Group as={Row} controlId="elevationDerate">
              <Form.Label column md={3}>
                Elevation Derate
              </Form.Label>
              <Col>
                <Form.Control
                  type="text"
                  name="elevationDerate"
                  value={formData.elevationDerate || ""}
                  disabled
                  placeholder={elevationDerate}
                />
              </Col>
            </Form.Group>
          </Col>
          <Col />
        </Row>
      </div>
      <div>
        <Row>
          {/* ******* Pump ******* */}
          <Col>
            <h4 className="subsection-header mb-3">Pump Details</h4>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpWatertronicsPartNumber">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpWatertronicsPartNumber")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Watertronics Part Number</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpWatertronicsPartNumber"
                      value={formData.pumpWatertronicsPartNumber || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Watertronics Part Number", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpWatertronicsPartNumber",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.pumpWatertronicsPartNumber
                          ? "tbd-highlight"
                          : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Watertronics Part Number"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpManufacturer">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpManufacturer")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Manufacturer</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpManufacturer"
                      value={formData.pumpManufacturer || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Manufacturer", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpManufacturer",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.pumpManufacturer ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Manufacturer"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpManufacturerModelNumber">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpManufacturerModelNumber")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Manufacturer Model Number</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpManufacturerModelNumber"
                      value={formData.pumpManufacturerModelNumber || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Manufacturer Model Number", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpManufacturerModelNumber",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.pumpManufacturerModelNumber
                          ? "tbd-highlight"
                          : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Manufacturer Model Number"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpType"
                      value={formData.pumpType || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Type", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={zoneTbdFields?.pumpType ? "tbd-highlight" : ""}
                      onChange={handleChange}
                      placeholder="Enter Type"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpEfficiency">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpEfficiency")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Efficiency</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpEfficiency"
                      value={formData.pumpEfficiency || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Efficiency", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpEfficiency",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.pumpEfficiency ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Efficiency"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpTDH">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpTDH")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>TDH</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpTDH"
                      value={formData.pumpTDH || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump TDH", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpTDH",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={zoneTbdFields?.pumpTDH ? "tbd-highlight" : ""}
                      onChange={handleChange}
                      placeholder="Enter TDH"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpFlow">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpFlow")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Flow</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpFlow"
                      value={formData.pumpFlow || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Flow", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpFlow",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={zoneTbdFields?.pumpFlow ? "tbd-highlight" : ""}
                      onChange={handleChange}
                      placeholder="Enter Flow"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpTPL">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpTPL")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>TPL (in)</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpTPL"
                      value={formData.pumpTPL || ""}
                      onFocus={handleFocus}
                      onBlur={(e) => {
                        const val = e.target.value;
                        if (!isValidTpl(val)) {
                          return;
                        }
                        handleBlur(e, "Pump TPL", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpTPL",
                          tableKey,
                        });
                      }}
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={`${zoneTbdFields?.pumpTPL ? "tbd-highlight" : ""} ${tplError ? "is-invalid" : ""}`}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTplError(!isValidTpl(val));
                        handleChange(e);
                      }}
                      placeholder="Enter TPL"
                    />
                    {tplError && (
                      <Form.Text className="text-danger">
                        Enter "TBD" or a numeric value
                      </Form.Text>
                    )}
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpStages">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpStages")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Stages</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpStages"
                      value={formData.pumpStages || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Stages", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpStages",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.pumpStages ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Stages"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpRPM">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpRPM")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>RPM</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpRPM"
                      value={formData.pumpRPM || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump RPM", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpRPM",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={zoneTbdFields?.pumpRPM ? "tbd-highlight" : ""}
                      onChange={handleChange}
                      placeholder="Enter RPM"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpDischargeSize">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpDischargeSize")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Discharge Size</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpDischargeSize"
                      value={formData.pumpDischargeSize || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Discharge Size", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpDischargeSize",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.pumpDischargeSize ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Discharge Size"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="pumpImpellerTrim">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PumpImpellerTrim")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Impeller Trim</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="pumpImpellerTrim"
                      value={formData.pumpImpellerTrim || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Pump Impeller Trim", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "PumpImpellerTrim",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.pumpImpellerTrim ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Impeller Trim"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="quantity">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Quantity")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Quantity</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="quantity"
                      value={formData.quantity || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Quantity", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Quantity",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={zoneTbdFields?.quantity ? "tbd-highlight" : ""}
                      onChange={handleChange}
                      placeholder="Enter Quantity"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
          </Col>
          {/* ******* Motor ******* */}
          <Col>
            <h4 className="subsection-header mb-3">Motor Details</h4>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorWatertronicsPartNumber">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorWatertronicsPartNumber")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Watertronics Part Number</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorWatertronicsPartNumber"
                      value={formData.motorWatertronicsPartNumber || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Watertronics Part Number", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorWatertronicsPartNumber",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorWatertronicsPartNumber
                          ? "tbd-highlight"
                          : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Watertronics Part Number"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorManufacturer">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorManufacturer")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Manufacturer</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorManufacturer"
                      value={formData.motorManufacturer || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Manufacturer", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorManufacturer",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorManufacturer ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Manufacturer"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorManufacturerModelNumber">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorManufacturerModelNumber")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Manufacturer Model Number</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorManufacturerModelNumber"
                      value={formData.motorManufacturerModelNumber || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Manufacturer Model Number", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorManufacturerModelNumber",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorManufacturerModelNumber
                          ? "tbd-highlight"
                          : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Manufacturer Model Number"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorHP">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorHP")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>HP</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorHP"
                      value={formData.motorHP || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor HP", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorHP",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={zoneTbdFields?.motorHP ? "tbd-highlight" : ""}
                      onChange={handleChange}
                      placeholder="Enter HP"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorEfficiency">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorEfficiency")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Efficiency</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorEfficiency"
                      value={formData.motorEfficiency || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Efficiency", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorEfficiency",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorEfficiency ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Efficiency"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorEnclosure">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorEnclosure")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Enclosure</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorEnclosure"
                      value={formData.motorEnclosure || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Enclosure", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorEnclosure",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorEnclosure ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Enclosure"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorFrame">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorFrame")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Frame</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorFrame"
                      value={formData.motorFrame || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Frame", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorFrame",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorFrame ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Frame"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorFLA">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorFLA")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>FLA</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorFLA"
                      value={formData.motorFLA || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor FLA", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorFLA",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={zoneTbdFields?.motorFLA ? "tbd-highlight" : ""}
                      onChange={handleChange}
                      placeholder="Enter FLA"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorCouplingType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorCouplingType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Coupling Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorCouplingType"
                      value={formData.motorCouplingType || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Coupling Type", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorCouplingType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorCouplingType ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Coupling Type"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorCouplingSize">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorCouplingSize")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Coupling Size</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorCouplingSize"
                      value={formData.motorCouplingSize || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Coupling Size", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorCouplingSize",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorCouplingSize ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Coupling Size"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorWindingHeaters">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorWindingHeaters")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Winding Heaters</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorWindingHeaters"
                      value={formData.motorWindingHeaters || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Winding Heaters", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorWindingHeaters",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorWindingHeaters
                          ? "tbd-highlight"
                          : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Winding Heaters"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorTempSwitch">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorTempSwitch")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Temp Switch</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorTempSwitch"
                      value={formData.motorTempSwitch || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Temp Switch", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorTempSwitch",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorTempSwitch ? "tbd-highlight" : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Temp Switch"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="motorVibrationSensor">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("MotorVibrationSensor")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Vibration Sensor</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="motorVibrationSensor"
                      value={formData.motorVibrationSensor || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Motor Vibration Sensor", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "MotorVibrationSensor",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                      className={
                        zoneTbdFields?.motorVibrationSensor
                          ? "tbd-highlight"
                          : ""
                      }
                      onChange={handleChange}
                      placeholder="Enter Vibration Sensor"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mb-3 mt-3">
          <Col>
            <Form.Group controlId="pumpOneConstruction">
              <Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip("PumpOneConstruction")}
                  delay={{ show: 300, hide: 200 }}
                >
                  <span>Pump One Construction</span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                name="pumpOneConstruction"
                value={formData.pumpOneConstruction || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Pump One Construction", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "PumpOneConstruction",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                className={
                  zoneTbdFields?.pumpOneConstruction ? "tbd-highlight" : ""
                }
                onChange={handleChange}
                placeholder="Enter Pump One Construction"
              />
            </Form.Group>
          </Col>

          <Col>
            <Form.Group controlId="pumpTwoConstruction">
              <Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip("PumpTwoConstruction")}
                  delay={{ show: 300, hide: 200 }}
                >
                  <span>Pump Two Construction</span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                name="pumpTwoConstruction"
                value={formData.pumpTwoConstruction || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Pump Two Construction", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "PumpTwoConstruction",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                className={
                  zoneTbdFields?.pumpTwoConstruction ? "tbd-highlight" : ""
                }
                onChange={handleChange}
                placeholder="Enter Pump Two Construction"
              />
            </Form.Group>
          </Col>

          <Col>
            <Form.Group controlId="startMethod">
              <Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip("StartMethod")}
                  delay={{ show: 300, hide: 200 }}
                >
                  <span>Start Method</span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                type="text"
                name="startMethod"
                value={formData.startMethod || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Start Method", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "StartMethod",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                className={zoneTbdFields?.startMethod ? "tbd-highlight" : ""}
                onChange={handleChange}
                placeholder="Enter Start Method"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Group controlId="comments">
              <Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip("Comments")}
                  delay={{ show: 300, hide: 200 }}
                >
                  <span>Comments</span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="comments"
                value={formData.comments || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Comments", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "Comments",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps && !roles.MechanicalEngineer)}
                className={zoneTbdFields?.comments ? "tbd-highlight" : ""}
                onChange={handleChange}
                placeholder="Enter Comments"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CustomPump;
